import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { GraduationCap, ShieldCheck, Student, User, Eye, EyeSlash, ArrowRight } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth, formatApiError } from "../lib/auth";
import api from "../lib/api";

export default function LoginPage() {
  const [tab, setTab] = useState("staff"); // staff | karyawan | wali
  // staff/karyawan fields
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  // wali fields
  const [nis, setNis] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (tab === "wali") {
        const { data } = await api.post("/wali/login", { nis: nis.trim(), phone: phone.trim() });
        localStorage.setItem("wali_token", data.token);
        localStorage.setItem("wali_siswa", JSON.stringify(data.siswa));
        toast.success(`Selamat datang, Wali ${data.siswa.name}`);
        navigate("/portal-wali");
      } else {
        await login(email, password);
        toast.success("Selamat datang!");
        navigate("/");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login gagal");
    } finally {
      setLoading(false);
    }
  };

  const TABS = [
    { key: "staff", label: "Staff Yayasan", icon: ShieldCheck },
    { key: "karyawan", label: "Guru / Karyawan", icon: Student },
    { key: "wali", label: "Wali Murid", icon: User },
  ];

  return (
    <div className="min-h-screen flex bg-white" data-testid="login-page">
      {/* Left panel: brand + gradient */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden" style={{ background: "linear-gradient(135deg, #48B8BD 0%, #0B8D95 100%)" }}>
        <div className="absolute -top-40 -left-40 w-96 h-96 rounded-full bg-white/10" />
        <div className="absolute -bottom-32 -right-24 w-80 h-80 rounded-full bg-white/5" />
        <div className="absolute top-1/3 right-8 w-32 h-32 rounded-full bg-white/5" />

        <div className="relative z-10 flex flex-col justify-between p-12 xl:p-16 text-white w-full">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-lg bg-white/15 backdrop-blur border border-white/25 flex items-center justify-center">
              <GraduationCap size={26} weight="duotone" />
            </div>
            <div>
              <div className="text-xs text-white/75 tracking-[0.2em] uppercase font-semibold">Yayasan</div>
              <div className="font-display text-xl font-bold">EduFoundation Suite</div>
            </div>
          </div>

          <div>
            <h1 className="font-display text-4xl xl:text-5xl font-black leading-[1.1] tracking-tight mb-6 max-w-lg">
              Kelola yayasan Anda selayaknya sistem digital modern.
            </h1>
            <p className="text-white/85 text-base leading-relaxed max-w-md">
              Keuangan, HR, Slip Gaji, Rapor Guru, Rekrutmen, dan Kalender Akademik — semua terintegrasi dalam satu platform.
            </p>
            <div className="flex flex-wrap gap-2.5 mt-8">
              {["Multi-Lembaga", "Cetak PDF", "Import Excel", "Portal Wali & Karyawan"].map((f) => (
                <span key={f} className="px-4 py-2 rounded-full bg-white/15 backdrop-blur border border-white/20 text-sm font-medium">{f}</span>
              ))}
            </div>
          </div>

          <div className="text-xs text-white/70">© 2026 EduFoundation</div>
        </div>
      </div>

      {/* Right panel: form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="h-11 w-11 rounded-lg bg-[#0B8D95] text-white flex items-center justify-center">
              <GraduationCap size={26} weight="duotone" />
            </div>
            <div>
              <div className="font-display text-lg font-bold text-gray-900">EduFoundation</div>
              <div className="text-xs text-gray-500 tracking-wider uppercase">Sistem Yayasan</div>
            </div>
          </div>

          <div className="mb-6">
            <div className="text-xs uppercase tracking-[0.25em] text-[#0B8D95] font-semibold mb-2">Masuk</div>
            <h2 className="font-display text-3xl sm:text-4xl font-black text-gray-900 tracking-tight mb-2">Halo, Selamat Datang</h2>
            <p className="text-gray-600 text-sm">Silakan pilih jenis akun Anda untuk masuk.</p>
          </div>

          {/* Tabs 3-way */}
          <div className="mb-6 p-1 bg-gray-100 rounded-full grid grid-cols-3 gap-1" data-testid="login-tabs">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`flex items-center justify-center gap-1.5 py-2.5 rounded-full text-xs sm:text-sm font-semibold transition-all ${active ? "bg-white text-[#0B8D95] shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                  data-testid={`login-tab-${t.key}`}
                >
                  <Icon size={15} weight="duotone" /> <span className="whitespace-nowrap">{t.label}</span>
                </button>
              );
            })}
          </div>

          <form onSubmit={handleSubmit} className="rounded-2xl border border-gray-100 bg-white shadow-sm p-6 space-y-4" data-testid="login-form">
            {tab === "wali" ? (
              <>
                <div>
                  <Label htmlFor="nis" className="text-sm font-semibold text-gray-800">NIS Siswa</Label>
                  <Input
                    id="nis"
                    type="text"
                    value={nis}
                    onChange={(e) => setNis(e.target.value)}
                    placeholder="Masukkan NIS anak Anda"
                    className="mt-1.5 h-12 bg-[#F0FAFA] border-transparent focus:bg-white focus:border-[#48B8BD] focus:ring-2 focus:ring-[#48B8BD]/20 rounded-xl"
                    data-testid="wali-nis-input"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-sm font-semibold text-gray-800">Nomor HP Wali</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="0812xxxxxxxx"
                    className="mt-1.5 h-12 bg-[#F0FAFA] border-transparent focus:bg-white focus:border-[#48B8BD] focus:ring-2 focus:ring-[#48B8BD]/20 rounded-xl"
                    data-testid="wali-phone-input"
                    required
                  />
                  <p className="text-[11px] text-gray-500 mt-1.5">Gunakan No. HP yang sama dengan yang terdaftar di data siswa.</p>
                </div>
              </>
            ) : (
              <>
                <div>
                  <Label htmlFor="email" className="text-sm font-semibold text-gray-800">
                    {tab === "karyawan" ? "Email / ID Karyawan" : "Email"}
                  </Label>
                  <Input
                    id="email"
                    type={tab === "karyawan" ? "text" : "email"}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={tab === "karyawan" ? "id_karyawan atau email" : "email Anda"}
                    className="mt-1.5 h-12 bg-[#F0FAFA] border-transparent focus:bg-white focus:border-[#48B8BD] focus:ring-2 focus:ring-[#48B8BD]/20 rounded-xl"
                    data-testid="login-email-input"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="password" className="text-sm font-semibold text-gray-800">Password</Label>
                  <div className="relative mt-1.5">
                    <Input
                      id="password"
                      type={showPw ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="h-12 bg-[#F0FAFA] border-transparent focus:bg-white focus:border-[#48B8BD] focus:ring-2 focus:ring-[#48B8BD]/20 rounded-xl pr-11"
                      data-testid="login-password-input"
                      required
                    />
                    <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-testid="login-toggle-pw">
                      {showPw ? <EyeSlash size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
              </>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-xl bg-gradient-to-r from-[#48B8BD] to-[#0B8D95] hover:opacity-95 text-white font-semibold text-base shadow-md shadow-[#0B8D95]/20"
              data-testid="login-submit-button"
            >
              {loading ? "Memproses..." : (<>Masuk <ArrowRight size={16} className="ml-1" weight="bold" /></>)}
            </Button>

            {/* Petunjuk generic — TIDAK mengekspos password/email spesifik */}
            <div className="text-xs text-gray-500 pt-2 border-t border-gray-100">
              {tab === "staff" && <span>Gunakan <b>email</b> dan <b>password</b> akun staf yayasan Anda. Belum punya akun? Hubungi Administrator.</span>}
              {tab === "karyawan" && <span>Login dengan <b>Email/ID Karyawan</b> dan <b>Password</b> yang diberikan HR.</span>}
              {tab === "wali" && <span>Login dengan <b>NIS anak</b> dan <b>No. HP wali</b> yang terdaftar di data siswa.</span>}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
